import dayjs from 'dayjs';
import { getContactTypeLabel } from 'utils/display-enum-label';
import type { TFunction } from 'i18next';
import type { Country } from 'store/common/models';
import type {
  TrackingCategory,
  ValidatedExcelTransaction,
} from 'store/organisation/models';
import { formatExcelTransactionDateForDisplay } from 'store/organisation/reducer/excelHandlers';
import {
  resolveAccountDisplayLabel,
  type AccountNameOption,
} from 'components/OrganisationDetails/transactions-tab/account-display';
import { resolveTrackingOptionDisplayLabel } from 'components/OrganisationDetails/transactions-tab/tracking-display';
import {
  ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY,
  ENTITY_TRANSACTIONS_ATTACHMENT_COLUMN_KEY,
  ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY,
  getSourceTypeDisplayLabel,
  TRACKING_CATEGORY_1_COLUMN_KEY,
  TRACKING_CATEGORY_2_COLUMN_KEY,
} from 'components/OrganisationDetails/transactions-tab/constants';
import {
  resolveItemDisplayLabel,
  type TransactionItemOption,
} from 'components/OrganisationDetails/transactions-tab/item-display';
import {
  resolveTaxDisplayLabel,
  type TaxNameOption,
} from 'components/OrganisationDetails/transactions-tab/tax-display';
import {
  formatTransactionAmountDisplay,
  formatTransactionQuantityDisplay,
} from 'utils/numberFormat';

export type EntityTransactionExportColumn = {
  key: string;
  title: string;
};

export type EntityTransactionExportContext = {
  accountNameByIdMap: Map<string, string>;
  accountNameOptions: AccountNameOption[];
  itemNameByIdMap: Map<string, string>;
  transactionItemOptions: TransactionItemOption[];
  taxNameByIdMap: Map<string, string>;
  taxNameOptions: TaxNameOption[];
  countries?: Country[];
  trackingCategories?: TrackingCategory[];
  formatNumber: (value: number) => string;
  formatNewContactLabel: (raw: unknown) => string;
  t: TFunction;
  findMatchingContactOption?: (
    ...values: Array<unknown>
  ) => { label: string; value: string } | undefined;
};

const AMOUNT_COLUMN_KEYS = new Set([
  'gross',
  'net',
  'tax',
  'exchangeRate',
  'discountAmount',
  'unitPrice',
]);

const DATE_COLUMN_KEYS = new Set([
  'transactionDate',
  'dueDate',
  'uploadDate',
]);

export function getEntityTransactionExportColumns(
  tableColumns: Array<{ key?: string; title?: unknown }>
): EntityTransactionExportColumn[] {
  return tableColumns
    .filter(
      (col) =>
        col.key &&
        col.key !== ENTITY_TRANSACTIONS_ATTACHMENT_COLUMN_KEY &&
        col.key !== ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY &&
        col.key !== ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY
    )
    .map((col) => ({
      key: String(col.key),
      title:
        typeof col.title === 'string' || typeof col.title === 'number'
          ? String(col.title)
          : String(col.key),
    }));
}

function trackingOptionLabel(
  optionId: string | null | undefined,
  optionName: string | null | undefined,
  trackingCategories: TrackingCategory[] | undefined,
  categoryIndex: number
): string {
  return resolveTrackingOptionDisplayLabel(
    optionId,
    optionName,
    trackingCategories,
    categoryIndex
  );
}

export function formatEntityTransactionExportCell(
  dataKey: string,
  row: ValidatedExcelTransaction,
  ctx: EntityTransactionExportContext
): string {
  const val = (row as Record<string, unknown>)[dataKey];
  const isEmpty = val == null || val === '';

  if (DATE_COLUMN_KEYS.has(dataKey)) {
    return formatExcelTransactionDateForDisplay(val ?? row[dataKey as keyof ValidatedExcelTransaction]);
  }

  if (dataKey === 'currency') {
    const code = val != null ? String(val).trim() : '';
    return code === '' ? '' : code;
  }

  if (dataKey === 'taxRate') {
    return !isEmpty ? `${val}%` : '';
  }

  if (dataKey === 'quantity') {
    if (isEmpty) return '';
    const num = parseFloat(String(val));
    return Number.isNaN(num) ? String(val) : formatTransactionQuantityDisplay(num);
  }

  if (AMOUNT_COLUMN_KEYS.has(dataKey)) {
    if (isEmpty) return '';
    const num = parseFloat(String(val));
    return Number.isNaN(num) ? String(val) : formatTransactionAmountDisplay(num);
  }

  if (dataKey === 'contactType' && !isEmpty) {
    return getContactTypeLabel(String(val), ctx.t);
  }

  if (dataKey === 'sourceType' && !isEmpty) {
    return getSourceTypeDisplayLabel(String(val));
  }

  if (dataKey === 'country' && !isEmpty) {
    const country = ctx.countries?.find((c) => c.iso === String(val));
    return country?.name ?? String(val);
  }

  if (dataKey === 'contactName') {
    const displayContactName = String(row.contactName ?? val ?? '').trim();
    if (!displayContactName) return '';
    const matchingContact = ctx.findMatchingContactOption?.(
      row.contactId,
      row.contactName,
      val
    );
    if (matchingContact === undefined) {
      return ctx.formatNewContactLabel(displayContactName);
    }
    return displayContactName;
  }

  if (dataKey === 'accountCode') {
    return resolveAccountDisplayLabel(
      row,
      val,
      ctx.accountNameByIdMap,
      ctx.accountNameOptions
    );
  }

  if (dataKey === 'itemId') {
    return resolveItemDisplayLabel(
      row,
      val,
      ctx.itemNameByIdMap,
      ctx.transactionItemOptions
    );
  }

  if (dataKey === 'taxName') {
    return resolveTaxDisplayLabel(
      row,
      val,
      ctx.taxNameByIdMap,
      ctx.taxNameOptions
    );
  }

  if (dataKey === TRACKING_CATEGORY_1_COLUMN_KEY) {
    const label = trackingOptionLabel(
      row.trackingOption1Id,
      row.trackingOption1Name,
      ctx.trackingCategories,
      0
    );
    return label === '-' ? '' : label;
  }

  if (dataKey === TRACKING_CATEGORY_2_COLUMN_KEY) {
    const label = trackingOptionLabel(
      row.trackingOption2Id,
      row.trackingOption2Name,
      ctx.trackingCategories,
      1
    );
    return label === '-' ? '' : label;
  }

  return isEmpty ? '' : String(val);
}

export async function exportEntityTransactionsToExcel(
  transactions: ValidatedExcelTransaction[],
  columns: EntityTransactionExportColumn[],
  ctx: EntityTransactionExportContext,
  sheetName: string,
  filename: string
): Promise<void> {
  if (!transactions.length || !columns.length) return;

  const XLSX = await import('xlsx');
  const headers = columns.map((col) => col.title);
  const exportRows = transactions.map((row) => {
    const exportRow: Record<string, string> = {};
    columns.forEach((col) => {
      exportRow[col.title] = formatEntityTransactionExportCell(col.key, row, ctx);
    });
    return exportRow;
  });

  const ws = XLSX.utils.json_to_sheet(exportRows, { header: headers });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  XLSX.writeFile(wb, filename);
}

export function getEntityTransactionsExportFilename(
  tab: 'CAPTURED' | 'REVIEWED' | 'POSTED' | 'ALL'
): string {
  const suffix =
    tab === 'CAPTURED'
      ? 'pending-review'
      : tab === 'REVIEWED'
      ? 'ready-to-post'
      : tab === 'POSTED'
      ? 'posted'
      : 'transactions';
  return `transactions-${suffix}-${dayjs().format('YYYY-MM-DD')}.xlsx`;
}
